import type { KeyboardEvent, RefObject } from "react";
import type { DeckorateCommand, RankedCommand } from "../../../types";
import { ACTION_CATALOG, SHAPE_CODES, ANYTIME_CODES, getCategoryAccent } from "../services/catalog";

/**
 * Presentational pieces shared by CommandPalette.tsx (in-pane overlay) and
 * AllActionsDialogApp.tsx (Office Dialog), which render the same catalog in
 * two separate bundles/CSS modules — kept together in one file since both
 * are only ever used alongside each other by those two callers.
 */

interface SearchInputRowProps {
  readonly inputRef: RefObject<HTMLInputElement>;
  readonly styles: Readonly<Record<string, string>>;
  readonly listboxId: string;
  readonly value: string;
  readonly activeIndex: number;
  readonly resultsCount: number;
  readonly testId: string;
  /** CommandPalette.tsx shows a 🔍 icon ahead of the input; AllActionsDialogApp.tsx doesn't. */
  readonly showIcon?: boolean;
  readonly onChange: (value: string) => void;
  readonly onClose: () => void;
  readonly onMove: (dir: number) => void;
  readonly onRunActive: () => void;
}

/**
 * The search row: an optional 🔍 icon plus the `role="combobox"` input
 * driving the results list below it. Arrow keys move the highlighted row,
 * Enter/Tab run it (Tab is deliberately repurposed — see CommandPalette.tsx's
 * focus-trap hook for why), and Escape closes.
 */
export function SearchInputRow({
  inputRef,
  styles,
  listboxId,
  value,
  activeIndex,
  resultsCount,
  testId,
  showIcon = false,
  onChange,
  onClose,
  onMove,
  onRunActive,
}: Readonly<SearchInputRowProps>) {
  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    switch (e.key) {
      case "Escape":
        e.preventDefault();
        onClose();
        break;
      case "ArrowDown":
        e.preventDefault();
        onMove(1);
        break;
      case "ArrowUp":
        e.preventDefault();
        onMove(-1);
        break;
      case "Enter":
      case "Tab":
        e.preventDefault();
        onRunActive();
        break;
      default:
        break;
    }
  }

  return (
    <div className={styles.searchRow}>
      {showIcon && (
        <span className={styles.searchIcon} aria-hidden="true">🔍</span>
      )}
      <input
        ref={inputRef}
        type="text"
        className={styles.input}
        placeholder="Search actions… try 'align left' or 'brand check'"
        autoComplete="off"
        spellCheck={false}
        role="combobox"
        aria-expanded="true"
        aria-haspopup="listbox"
        aria-controls={listboxId}
        aria-activedescendant={resultsCount > 0 ? `${listboxId}-option-${activeIndex}` : undefined}
        aria-autocomplete="list"
        value={value}
        data-testid={testId}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
      />
    </div>
  );
}

interface ActionResultsListProps {
  readonly listboxId: string;
  readonly results: readonly RankedCommand[];
  readonly activeIndex: number;
  readonly styles: Readonly<Record<string, string>>;
  readonly testIdPrefix: string;
  /** CommandPalette.tsx shows a "Pending" badge per row; AllActionsDialogApp.tsx
   * doesn't (its footer already documents which codes are wired up). */
  readonly showPendingBadge?: boolean;
  readonly onHover: (index: number) => void;
  readonly onSelect: (cmd: RankedCommand) => void;
  readonly onToggleFavorite: (id: string) => void;
}

/**
 * The shared `role="listbox"` action list. A native <select>/<datalist>
 * can't render icons/badges/favorite buttons — role="listbox"/"option" is
 * the standard rich-combobox pattern, with each caller's search <input> as
 * the combobox and aria-activedescendant tracking the highlighted option
 * (real DOM focus stays on the input).
 */
export function ActionResultsList({
  listboxId,
  results,
  activeIndex,
  styles,
  testIdPrefix,
  showPendingBadge = false,
  onHover,
  onSelect,
  onToggleFavorite,
}: Readonly<ActionResultsListProps>) {
  return (
    <div id={listboxId} className={styles.results} role="listbox" aria-label="Matching actions"> {/* NOSONAR(typescript:S6819) — see comment above */}
      {results.length === 0 && <div className={styles.empty}>No matching actions</div>}
      {results.map((cmd, idx) => {
        const isPending = cmd.kind === "pending";
        const accent = getCategoryAccent(cmd.category);
        return (
          <div
            key={cmd.id}
            id={`${listboxId}-option-${idx}`}
            role="option"
            tabIndex={-1}
            aria-selected={idx === activeIndex}
            title={cmd.category}
            className={`${styles.item} ${idx === activeIndex ? styles.itemActive : ""} ${isPending ? styles.itemPending : ""}`}
            data-testid={`${testIdPrefix}-item-${cmd.id}`}
            onMouseEnter={() => onHover(idx)}
            onMouseDown={(e) => {
              e.preventDefault();
              onSelect(cmd);
            }}
          >
            <span className={styles.itemIcon} style={{ background: accent.bg, color: accent.fg }} aria-hidden="true">
              {cmd.icon}
            </span>
            <div className={styles.itemBody}>
              <div className={styles.itemLabel}>{cmd.label}</div>
            </div>
            <div className={styles.itemMetaRight}>
              {cmd.code && <span className={styles.codeBadge}>{cmd.code}</span>}
              <span className={styles.categoryText}>{cmd.category}</span>
            </div>
            {showPendingBadge && isPending && <span className={styles.pendingBadge}>Pending</span>}
            <button
              type="button"
              className={`${styles.starBtn} ${cmd.favorite ? styles.starBtnActive : ""}`}
              aria-pressed={cmd.favorite}
              aria-label={cmd.favorite ? `Remove ${cmd.label} from favorites` : `Add ${cmd.label} to favorites`}
              data-testid={`${testIdPrefix}-favorite-${cmd.id}`}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.preventDefault();
                onToggleFavorite(cmd.id);
              }}
            >
              {cmd.favorite ? "★" : "☆"}
            </button>
          </div>
        );
      })}
    </div>
  );
}

interface DialogTopbarProps {
  readonly styles: Readonly<Record<string, string>>;
  readonly onClose: () => void;
  readonly closeTestId: string;
  readonly closeAriaLabel: string;
}

/** The "All actions ✕" header bar atop both the search panel and the ai-preview panel. */
export function DialogTopbar({ styles, onClose, closeTestId, closeAriaLabel }: Readonly<DialogTopbarProps>) {
  return (
    <div className={styles.topbar}>
      <span className={styles.title}>All actions</span>
      <button
        type="button"
        className={styles.close}
        onClick={onClose}
        title="Close (Esc)"
        aria-label={closeAriaLabel}
        data-testid={closeTestId}
      >
        ✕
      </button>
    </div>
  );
}

interface ActionCatalogFooterProps {
  readonly styles: Readonly<Record<string, string>>;
  /** CommandPalette.tsx shows arrow/enter/esc key hints; AllActionsDialogApp.tsx omits them. */
  readonly showKeyboardHints?: boolean;
  /** Size of the list being searched — Deckorate's catalog unless the pane's merged one (ST-05). */
  readonly count?: number;
  /** The codes are Deckorate's: hidden when its actions aren't in the list (ST-05, Deckorate off). */
  readonly showCodes?: boolean;
}

/** The footer's catalog count + direct-key code hints, shared by both search panels. */
export function ActionCatalogFooter({
  styles,
  showKeyboardHints = false,
  count = ACTION_CATALOG.length,
  showCodes = true,
}: Readonly<ActionCatalogFooterProps>) {
  return (
    <div className={styles.footer}>
      <div>
        All {count} actions listed · fuzzy search (typos OK) · ☆ favorites float
      </div>
      {showKeyboardHints && (
        <div>
          <kbd className={styles.kbd}>↑</kbd><kbd className={styles.kbd}>↓</kbd> navigate ·{" "}
          <kbd className={styles.kbd}>↵</kbd> run · <kbd className={styles.kbd}>Esc</kbd> close
        </div>
      )}
      {showCodes && (
        <>
          <div className={styles.codeHints}>
            Codes work as direct keys — shapes:{" "}
            {SHAPE_CODES.map((code) => (
              <kbd key={code} className={styles.kbd}>{code}</kbd>
            ))}
          </div>
          <div className={styles.codeHints}>
            nothing selected:{" "}
            {ANYTIME_CODES.map((code) => (
              <kbd key={code} className={styles.kbd}>{code}</kbd>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

interface AiPreviewPanelProps {
  readonly command: DeckorateCommand;
  readonly styles: Readonly<Record<string, string>>;
  readonly panelRef: RefObject<HTMLDivElement>;
  readonly panelTestId: string;
  readonly closeTestId: string;
  readonly closeAriaLabel: string;
  readonly onBack: () => void;
  readonly onClose: () => void;
}

/**
 * The "ai-preview" info dialog — swaps in for the search list to surface an
 * AI Designer action's label/description as a real UI, ahead of the actual
 * Office.js/backend work landing separately.
 */
export function AiPreviewPanel({
  command,
  styles,
  panelRef,
  panelTestId,
  closeTestId,
  closeAriaLabel,
  onBack,
  onClose,
}: Readonly<AiPreviewPanelProps>) {
  const accent = getCategoryAccent(command.category);
  return (
    <dialog open className={styles.overlay} aria-modal="true" aria-label={command.label}>
      <div ref={panelRef} className={styles.panel} data-testid={panelTestId}>
        <div className={styles.topbar}>
          <button type="button" className={styles.backBtn} onClick={onBack} aria-label="Back to search">
            ← Back
          </button>
          <button
            type="button"
            className={styles.close}
            onClick={onClose}
            title="Close (Esc)"
            aria-label={closeAriaLabel}
            data-testid={closeTestId}
          >
            ✕
          </button>
        </div>

        <div className={styles.results} data-testid="ai-preview-dialog">
          <div className={styles.item} style={{ cursor: "default" }}>
            <span className={styles.itemIcon} style={{ background: accent.bg, color: accent.fg }} aria-hidden="true">
              {command.icon}
            </span>
            <div className={styles.itemBody}>
              <div className={styles.itemLabel}>{command.label}</div>
              <div className={styles.itemMeta}>{command.category}</div>
            </div>
          </div>
          {command.description && (
            <div className={styles.notice} style={{ borderBottom: "none" }}>
              {command.description}
            </div>
          )}
          <output className={styles.notice} style={{ borderBottom: "none" }}>
            This AI action is coming soon — the dialog is ready, generation isn't wired up yet.
          </output>
        </div>

        <div className={styles.footer}>
          <span><kbd className={styles.kbd}>Esc</kbd> Close</span>
        </div>
      </div>
    </dialog>
  );
}
